import { create } from 'zustand';
import { persist } from 'zustand/middleware';
import type { User, Customer, Product } from '../types';
import authService from '../services/auth.service';
import adminService from '../services/admin.service';

interface AuthStore {
  user: User | null;
  customer: Customer | null;
  customers: Customer[];
  accessibleProducts: Product[];
  isCustomerAdmin: boolean;
  isAuthenticated: boolean;
  isLoading: boolean;
  error: string | null;

  // Actions
  initialize: () => Promise<void>;
  selectCustomer: (customer: Customer) => Promise<void>;
  logout: () => Promise<void>;
  clearError: () => void;
}

export const useAuthStore = create<AuthStore>()(
  persist(
    (set) => ({
      user: null,
      customer: null,
      customers: [],
      accessibleProducts: [],
      isCustomerAdmin: false,
      isAuthenticated: false,
      isLoading: true,
      error: null,

      initialize: async () => {
        try {
          set({ isLoading: true, error: null });
          const response = await authService.getCurrentUser();

          // If user has only 1 product and is not an admin, redirect to that product directly
          const isAdmin = response.isCustomerAdmin ||
            response.user.roles.some(r => ['GlobalAdmin', 'CustomerAdmin', 'ProgramAdmin', 'UserAdmin'].includes(r));

          if (!isAdmin && response.accessibleProducts.length === 1) {
            const product = response.accessibleProducts[0];
            const productUrls: Record<string, string> = {
              'LCL': 'https://contractlens.lynki.no',
              'CONTRACTLENS': 'https://contractlens.lynki.no',
            };
            const redirectUrl = productUrls[product.code?.toUpperCase() || ''];
            if (redirectUrl) {
              window.location.href = redirectUrl;
              return;
            }
          }

          set({
            user: response.user,
            customers: response.customers,
            customer: response.selectedCustomer || response.customers[0] || null,
            accessibleProducts: response.accessibleProducts,
            isCustomerAdmin: response.isCustomerAdmin,
            isAuthenticated: true,
            isLoading: false,
          });
        } catch {
          set({
            user: null,
            customers: [],
            customer: null,
            accessibleProducts: [],
            isCustomerAdmin: false,
            isAuthenticated: false,
            isLoading: false,
            error: 'Failed to authenticate',
          });
        }
      },

      selectCustomer: async (customer: Customer) => {
        try {
          await authService.selectCustomer(customer.id);
        } catch {
          // GlobalAdmin may not have a CustomerUser record - that's OK, just set locally
        }
        set({ customer });

        // For GlobalAdmin: fetch the customer's licensed products so sidebar updates
        const state = useAuthStore.getState();
        const isGlobalAdmin = state.user?.roles?.includes('GlobalAdmin');
        if (isGlobalAdmin) {
          try {
            const { licenses } = await adminService.getLicenses(customer.id);
            // Derive unique products from licenses
            const productMap = new Map<string, Product>();
            for (const lic of licenses) {
              if (lic.product && !productMap.has(lic.product.id)) {
                productMap.set(lic.product.id, {
                  id: lic.product.id,
                  name: lic.product.name,
                  code: lic.product.code,
                  shortName: lic.product.shortName,
                  url: lic.product.code
                    ? `/products/${lic.product.code.toLowerCase()}`
                    : undefined,
                });
              }
            }
            set({ accessibleProducts: Array.from(productMap.values()) });
          } catch {
            // If license fetch fails, keep existing products
          }
        }
      },

      logout: async () => {
        try {
          await authService.logout();
        } finally {
          set({
            user: null,
            customer: null,
            customers: [],
            accessibleProducts: [],
            isCustomerAdmin: false,
            isAuthenticated: false,
          });
          // Redirect to auth login page
          const clientId = import.meta.env.VITE_AUTH_CLIENT_ID || '11111111-1111-1111-1111-111111111111';
          window.location.href = `${import.meta.env.VITE_AUTH_WEB_URL || 'https://auth.lynki.no'}/login?clientId=${clientId}&returnUrl=${encodeURIComponent(window.location.origin)}`;
        }
      },

      clearError: () => set({ error: null }),
    }),
    {
      name: 'lynki-auth',
      partialize: (state) => ({ customer: state.customer }),
    }
  )
);

export default useAuthStore;
